import React, { useState, useEffect } from 'react';
import { instrumentService, priceStreamService } from '../../../services/api/instrumentService';
import { productService } from '../../../services/api/productService';
import { useSession } from '../../../components/session/SessionManager';
import type { MarketInstrument, MarketType, PriceUpdate } from '../../../types/instrument';
import { ASSET_CLASS_MAP } from '../../../types/instrument';
import './MarketDiscovery.css';

interface MarketDiscoveryProps {
  isMobile: boolean;
  selectedMarketType: string;
  selectedProduct: string;
  selectedInstrument: string;
  currentPrice: string;
  desktopMarketType: string;
  desktopInstruments: Array<{
    id: string;
    market: string;
    symbol: string;
    name: string;
    price: string;
    change: string;
  }>;
  onInstrumentSelect?: (instrument: { id: string; market: string; symbol: string; name: string; price: string; change: string }) => void;
  onShowMarketModal: () => void;
  onMarketTypeChange: (market: string) => void;
  onRemoveInstrument: (symbol: string) => void;
}

export const MarketDiscovery: React.FC<MarketDiscoveryProps> = ({
  isMobile,
  selectedMarketType,
  selectedProduct,
  selectedInstrument,
  currentPrice,
  desktopMarketType,
  desktopInstruments,
  onShowMarketModal,
  onMarketTypeChange,
  onRemoveInstrument,
  onInstrumentSelect
}) => {
  const { isAuthenticated, loading: sessionLoading } = useSession();
  const [realTimeInstruments, setRealTimeInstruments] = useState<Map<string, MarketInstrument>>(new Map());
  const [filteredInstruments, setFilteredInstruments] = useState<Array<{
    id: string;
    market: string;
    symbol: string;
    name: string;
    price: string;
    change: string;
  }>>([]);
  const [availableMarketTypes, setAvailableMarketTypes] = useState<string[]>(['Forex', 'Indices', 'Crypto', 'Commodities', 'Synthetic']);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Load available market types when component mounts and user is authenticated
  useEffect(() => {
    if (!isAuthenticated || sessionLoading) {
      return;
    }
    
    loadAvailableMarketTypes();
  }, [isAuthenticated, sessionLoading]);

  // Load real-time data for desktop instruments - only when instruments change AND user is authenticated
  useEffect(() => {
    // Don't make API calls if not authenticated or session is still loading
    if (!isAuthenticated || sessionLoading) {
      return;
    }

    if (desktopInstruments.length === 0) {
      setRealTimeInstruments(new Map());
      priceStreamService.stopPriceStream();
      return;
    }
    
    loadRealTimeData();

    return () => {
      // Cleanup price streaming on unmount
      priceStreamService.stopPriceStream();
    };
  }, [isAuthenticated, sessionLoading, desktopInstruments.map(inst => inst.symbol).join(',')]); // Include auth state

  // Filter instruments by product compatibility when product or instruments change
  useEffect(() => {
    filterInstrumentsByProduct();
  }, [selectedProduct, desktopInstruments]);

  // Setup price streaming separately after real-time data is loaded
  useEffect(() => {
    if (realTimeInstruments.size === 0) return;
    
    setupPriceStreaming();

    return () => {
      priceStreamService.stopPriceStream();
    };
  }, [realTimeInstruments.size]); // Only when we have instruments loaded

  // Load available market types from API
  const loadAvailableMarketTypes = async () => {
    try {
      const marketTypes = await instrumentService.getAvailableMarketTypes();
      setAvailableMarketTypes(marketTypes);
    } catch (error) {
      console.error('Failed to load available market types:', error);
      // Keep default fallback values
    }
  };

  // Load real-time data for current instruments
  const loadRealTimeData = async () => {
    if (desktopInstruments.length === 0 || !isAuthenticated) return;
    
    setLoading(true);
    setApiError(null);
    
    try {
      // For each market type, load instruments with current prices
      const marketTypes = [...new Set(desktopInstruments.map(inst => inst.market))];
      
      for (const marketType of marketTypes) {
        const assetClass = ASSET_CLASS_MAP[marketType as MarketType];
        if (assetClass) {
          try {
            const instruments = await instrumentService.getInstrumentsByAssetClass(assetClass);
            
            // Update real-time instruments map
            instruments.forEach(instrument => {
              setRealTimeInstruments(prev => new Map(prev.set(instrument.symbol, instrument)));
            });
          } catch (marketError) {
            console.warn(`Failed to load ${marketType} instruments:`, marketError);
            // Continue with other market types even if one fails
          }
        }
      }
    } catch (error) {
      console.error('Failed to load real-time market data:', error);
      setApiError('Failed to load market data. Using cached information.');
    } finally {
      setLoading(false);
    }
  };

  // Filter instruments by product compatibility
  const filterInstrumentsByProduct = async () => {
    if (desktopInstruments.length === 0) {
      setFilteredInstruments([]);
      return;
    }

    try {
      // Call productService to get available instruments for the selected product
      const availableInstruments = await productService.getAvailableInstruments(selectedProduct);
      
      if (availableInstruments.length === 0) {
        // If no specific instruments are defined, show all instruments
        setFilteredInstruments(desktopInstruments);
        return;
      }
      
      // Filter instruments based on product compatibility
      const filtered = desktopInstruments.filter(instrument => 
        availableInstruments.includes(instrument.id)
      );
      
      setFilteredInstruments(filtered);
    } catch (error) {
      console.error('Failed to filter instruments by product:', error);
      // On error, show all instruments to avoid breaking the UI
      setFilteredInstruments(desktopInstruments);
    }
  };

  // Setup price streaming for visible instruments
  const setupPriceStreaming = () => {
    const instrumentIds: string[] = [];
    
    // Collect instrument IDs from real-time data
    realTimeInstruments.forEach((instrument) => {
      instrumentIds.push(instrument.instrumentId);
    });

    if (instrumentIds.length === 0) return;

    // Start price streaming
    priceStreamService.startPriceStream(instrumentIds, (priceUpdate: PriceUpdate) => {
      handlePriceUpdate(priceUpdate);
    });
  };

  // Handle real-time price updates
  const handlePriceUpdate = (priceUpdate: PriceUpdate) => {
    setRealTimeInstruments(prev => {
      const updated = new Map(prev);
      
      // Find instrument by ID and update price
      for (const [symbol, instrument] of updated.entries()) {
        if (instrument.instrumentId === priceUpdate.instrumentId) {
          const midPrice = (priceUpdate.bid + priceUpdate.ask) / 2;
          
          // Calculate price change (simplified)
          const currentPrice = parseFloat(instrument.price.replace(/[^\d.-]/g, ''));
          const change = currentPrice > 0 ? ((midPrice - currentPrice) / currentPrice) * 100 : 0;
          
          updated.set(symbol, {
            ...instrument,
            price: formatPrice(midPrice),
            change: formatChange(change),
            bid: priceUpdate.bid,
            ask: priceUpdate.ask
          });
          break;
        }
      }
      
      return updated;
    });
  };

  // Format price for display
  const formatPrice = (price: number): string => {
    if (price >= 1000) {
      return price.toLocaleString('en-US', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      });
    } else if (price >= 1) {
      return price.toFixed(4);
    } else {
      return price.toFixed(6);
    }
  };

  // Format change percentage
  const formatChange = (changePercent: number): string => {
    if (changePercent === 0) return '0.00%';
    const formatted = Math.abs(changePercent).toFixed(2);
    return changePercent >= 0 ? `+${formatted}%` : `-${formatted}%`;
  };

  // Get enhanced instrument data with real-time prices
  const getEnhancedInstrument = (instrument: { market: string; symbol: string; name: string; price: string; change: string }) => {
    const realTimeData = realTimeInstruments.get(instrument.symbol);
    
    if (realTimeData) {
      return {
        ...instrument,
        price: realTimeData.price,
        change: realTimeData.change
      };
    }
    
    return instrument;
  };

  // Get current selected instrument with real-time data
  const getCurrentInstrumentPrice = () => {
    if (!selectedInstrument) return currentPrice;
    
    // Find the selected instrument by ID to get the symbol for lookup
    const selectedInstrumentData = desktopInstruments.find(inst => inst.id === selectedInstrument);
    if (!selectedInstrumentData) return currentPrice;
    
    const realTimeData = realTimeInstruments.get(selectedInstrumentData.symbol);
    return realTimeData ? realTimeData.price : currentPrice;
  };

  // Get current selected instrument name for display
  const getCurrentInstrumentName = () => {
    if (!selectedInstrument) return selectedInstrument;
    
    // Find the selected instrument by ID to get the name
    const selectedInstrumentData = desktopInstruments.find(inst => inst.id === selectedInstrument);
    return selectedInstrumentData ? (selectedInstrumentData.name || selectedInstrumentData.symbol) : selectedInstrument;
  };


  return (
    <section className="market-discovery">
      {/* Mobile Market Selector */}
      {isMobile ? (
        <button 
          className="mobile-market-selector"
          onClick={onShowMarketModal}
        >
          <span className="market-type">{selectedMarketType}</span>
          <span className="selected-instrument">
            {getCurrentInstrumentName()} <span className="current-price">{getCurrentInstrumentPrice()}</span>
          </span>
          <span className="dropdown-icon">▼</span>
        </button>
      ) : (
        <>
          <div className="market-tabs">
            {availableMarketTypes.map((market) => (
              <button
                key={market}
                className={`market-tab${desktopMarketType === market ? ' active' : ''}`}
                onClick={() => onMarketTypeChange(market)}
              >
                {market}
              </button>
            ))}
            <button
              className={`market-tab add-market-btn`}
              onClick={onShowMarketModal}
              style={{ marginLeft: 8, fontWeight: 600 }}
            >
              + Add Market
            </button>
          </div>
          <div className="selected-instruments">
            {(loading || sessionLoading) && (
              <div style={{ color: '#888', padding: '8px 0', fontSize: '12px' }}>
                {sessionLoading ? 'Authenticating...' : 'Loading real-time prices...'}
              </div>
            )}
            {apiError && (
              <div style={{ color: '#ff9800', padding: '4px 0', fontSize: '11px' }}>
                {apiError}
              </div>
            )}
            {filteredInstruments
              .filter(inst => inst.market === desktopMarketType)
              .map(inst => {
                const enhancedInst = getEnhancedInstrument(inst);
                const isActive = inst.id === selectedInstrument;
                return (
                  <div
                    className={`instrument-chip${isActive ? ' active' : ''}`}
                    key={inst.id}
                    style={{
                      border: isActive ? '2px solid #00d4ff' : '1px solid #222',
                      background: isActive ? '#1a2b3a' : undefined,
                      cursor: onInstrumentSelect ? 'pointer' : 'default'
                    }}
                    onClick={() => onInstrumentSelect && onInstrumentSelect(inst)}
                  >
                    <span>{inst.name || inst.symbol}</span>
                    <span className="price-display">{enhancedInst.price}</span>
                    <span className={`price-change ${enhancedInst.change.startsWith('+') ? 'positive' : 'negative'}`}>
                      {enhancedInst.change}
                    </span>
                    <button
                      className="remove-instrument-btn"
                      style={{ marginLeft: 8, background: 'none', border: 'none', color: '#aaa', fontSize: 16, cursor: 'pointer' }}
                      onClick={e => { e.stopPropagation(); onRemoveInstrument(inst.symbol); }}
                      aria-label={`Remove ${inst.symbol}`}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            {/* Show message if no instruments for selected market */}
            {filteredInstruments.filter(inst => inst.market === desktopMarketType).length === 0 && (
              <div style={{ color: '#888', padding: '12px 0' }}>
                {desktopInstruments.filter(inst => inst.market === desktopMarketType).length === 0 
                  ? 'No instruments for this market. Click + Add Market to add.'
                  : `No instruments available for ${selectedProduct} in this market.`
                }
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
};
